import React, { useEffect, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-quill-new/dist/quill.snow.css';
import postApi from '../../api/postApi';
import { normalizeForEditor } from '../../utils/normalizePostContent';
import { AUDIENCE_OPTIONS } from '../../utils/audience-visibility';

/** Lấy câu lỗi backend trả về; hết cách thì dùng câu mặc định (đừng nuốt im). */
function apiMessage(error, fallback) {
    return error?.response?.data?.message || error?.message || fallback;
}

const fontSizeArr = [
    '8px',
    '9px',
    '10px',
    '12px',
    '14px',
    '16px',
    '20px',
    '24px',
    '32px',
    '42px',
    '54px',
    '68px',
    '84px',
    '98px',
];
const Size = Quill.import('attributors/style/size');
Size.whitelist = fontSizeArr;
Quill.register(Size, true);

const modules = {
    toolbar: [
        [{ header: [1, 2, false] }],
        [{ size: fontSizeArr }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [
            { list: 'ordered' },
            { list: 'bullet' },
            { indent: '-1' },
            { indent: '+1' },
        ],
        ['link'],
        ['clean'],
    ],
    // matchVisual:false stops Quill from baking extra line breaks/paragraphs into
    // the content when it converts loaded HTML. With it ON (the default), a long
    // URL link that wraps visually gets split onto its own line — the "xuống hàng"
    // bug in the editor. Together with normalizeForEditor's link reassembly, URL
    // links now stay inline on the same line as their label.
    clipboard: { matchVisual: false },
};

const formats = [
    'header',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
];

function PostEditor() {
    const { postid } = useParams();
    const [title, setTitle] = useState('');
    const [summary, setSummary] = useState('');
    const [content, setContent] = useState('');
    const [file, setFile] = useState();
    // Ảnh bìa hiện tại (lấy từ server khi sửa) + preview ảnh mới vừa chọn.
    const [imagePath, setImagePath] = useState('');
    const [filePreview, setFilePreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    // Bài chỉ hiện với đúng đối tượng (backend lọc ở /post/get_post)
    const [audience, setAudience] = useState('all');
    const [audienceDays, setAudienceDays] = useState('');
    // Only render the editor once initial content is ready, so its
    // `defaultValue` captures the real HTML on first (and only) mount.
    // New post (no postid) is ready immediately; edit waits for fetch.
    const [loaded, setLoaded] = useState(!postid);
    // Track Quill content via ref to avoid controlled re-render scrambling
    const contentRef = React.useRef('');
    const navigate = useNavigate();

    // Số ngày chỉ có nghĩa với "user mới" — loại khác gửi rỗng để backend xoá giá trị cũ
    const appendAudience = (formData) => {
        formData.append('audience', audience);
        formData.append('audienceDays', audience === 'new_user' ? audienceDays : '');
    };

    const onSubmit = async () => {
        const currentContent = normalizeForEditor(contentRef.current);
        // Trước đây thiếu trường nào là `return` im lặng: bấm Đăng bài không gửi request,
        // không báo gì, người viết tưởng hệ thống hỏng. Phải nói rõ còn thiếu cái gì.
        const missing = [
            !title && 'tiêu đề',
            !summary && 'tóm tắt',
            !currentContent && 'nội dung',
            !file && 'ảnh bìa',
        ].filter(Boolean);
        if (missing.length) {
            toast.warn(`Chưa đăng được: còn thiếu ${missing.join(', ')}.`);
            return;
        }
        const formData = new FormData();
        formData.append('title', title);
        formData.append('summary', summary);
        formData.append('content', currentContent);
        formData.append('image', file);
        formData.append('category', 'guide');
        appendAudience(formData);

        setSaving(true);
        try {
            await postApi.newPost(formData);
            toast.success('Đã đăng bài');
            navigate('/post');
        } catch (error) {
            toast.error(apiMessage(error, 'Đăng bài thất bại'));
        } finally {
            setSaving(false);
        }
    };

    const onUpdatePost = async () => {
        const currentContent = normalizeForEditor(contentRef.current);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('summary', summary);
        formData.append('content', currentContent);
        if (file) formData.append('image', file);
        formData.append('postId', postid);
        appendAudience(formData);

        setSaving(true);
        try {
            await postApi.updatePost(formData);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (error) {
            toast.error(apiMessage(error, 'Lưu bài thất bại'));
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!postid) return;
        setLoaded(false);
        const fetchPost = async () => {
            try {
                // Không lọc category: bài của trang sàn (shopee/lazada/tiktok/
                // shopeefood) cũng phải mở sửa được, không riêng 'guide'.
                const { data } = await postApi.getGuidePost(`postId=${postid}`);
                const post = data.post?.[0];
                if (!post) return;
                const fixedContent = normalizeForEditor(post.content || '');
                setTitle(post.title || '');
                setSummary(post.summary || '');
                setContent(fixedContent);
                setImagePath(post.imagePath || '');
                setAudience(post.audience || 'all');
                setAudienceDays(post.audienceDays ? String(post.audienceDays) : '');
                contentRef.current = fixedContent;
            } catch (error) {
                toast.error(apiMessage(error, 'Không tải được nội dung bài viết'));
            } finally {
                setLoaded(true);
            }
        };
        fetchPost();
    }, [postid]);

    // Tạo URL preview cho ảnh vừa chọn; revoke khi đổi ảnh để tránh leak.
    useEffect(() => {
        if (!file) {
            setFilePreview('');
            return;
        }
        const url = URL.createObjectURL(file);
        setFilePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div className="flex flex-col h-dvh bg-surface-sunken">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b shadow-sm flex-shrink-0">
                <button
                    onClick={() => navigate('/post')}
                    className="text-content-3 hover:text-content transition-colors"
                    title="Quay lại"
                >
                    ←
                </button>
                <h3 className="font-semibold text-lg text-content">
                    {postid ? 'Chỉnh sửa bài viết' : 'Tạo bài viết mới'}
                </h3>
            </div>

            {/* Form fields */}
            <div className="flex flex-col gap-3 px-4 pt-3 pb-2 bg-surface border-b flex-shrink-0">
                <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg border border-line focus:outline-none focus:ring-2 focus:ring-primary-300 text-content placeholder-gray-400"
                    name="title"
                    placeholder="Tiêu đề bài viết"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg border border-line focus:outline-none focus:ring-2 focus:ring-primary-300 text-content placeholder-gray-400"
                    name="summary"
                    placeholder="Mô tả ngắn"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                />
                <div className="flex items-center gap-3">
                    <label className="text-sm text-content-3 whitespace-nowrap">Ảnh bìa:</label>
                    {/* Preview: ưu tiên ảnh mới chọn, fallback ảnh hiện tại trên server.
                        contain để xem trọn ảnh, không cắt. */}
                    {(filePreview || imagePath) && (
                        <div className="relative w-28 aspect-video rounded-md overflow-hidden bg-surface-muted flex-shrink-0 border border-line">
                            <img
                                src={
                                    filePreview ||
                                    `${process.env.REACT_APP_API_PUBLIC_URL}/img/post-thumbs/${imagePath}`
                                }
                                alt="Ảnh bìa"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="text-sm text-content-2 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                    />
                </div>
                {/* Hiển thị cho ai — backend lọc khi trả danh sách bài */}
                <div className="flex items-center gap-3">
                    <label className="text-sm text-content-3 whitespace-nowrap">Hiển thị cho:</label>
                    <select
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-line text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary-300"
                    >
                        {AUDIENCE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    {audience === 'new_user' && (
                        <input
                            type="number"
                            min="1"
                            max="365"
                            value={audienceDays}
                            onChange={(e) => setAudienceDays(e.target.value)}
                            placeholder="7 ngày"
                            className="w-24 px-3 py-2 rounded-lg border border-line text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                    )}
                </div>
            </div>

            {/* Editor — fills remaining space; min-h-0 lets flex child shrink + scroll */}
            <div className="flex-1 min-h-0 flex flex-col">
                {loaded ? (
                    <ReactQuill
                        key={postid || 'new'}
                        modules={modules}
                        formats={formats}
                        theme="snow"
                        // Uncontrolled: set initial HTML once via defaultValue so
                        // re-renders (saving/saved/typing in other fields) never reset
                        // the editor contents or cursor. Read latest via contentRef.
                        defaultValue={content}
                        onChange={(value) => {
                            contentRef.current = value;
                        }}
                        // Scroll lives on .ql-editor (not .ql-container) so the
                        // link tooltip (Ctrl+K popup, absolutely positioned inside
                        // .ql-container) isn't clipped/wrapped, which broke the UI.
                        // Tooltip laid out as a flex row (label + input + Save on one
                        // line); input shrinks (flex-1 min-w-0) instead of forcing the
                        // Save button to wrap on narrow/mobile widths. max-w keeps the
                        // whole popup inside the editor box.
                        className="flex-1 min-h-0 flex flex-col [&_.ql-container]:flex-1 [&_.ql-container]:min-h-0 [&_.ql-container]:flex [&_.ql-container]:flex-col [&_.ql-editor]:flex-1 [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:break-words [&_.ql-editor_a]:break-all [&_.ql-tooltip:not(.ql-hidden)]:!flex [&_.ql-tooltip]:items-center [&_.ql-tooltip]:gap-1.5 [&_.ql-tooltip]:whitespace-nowrap [&_.ql-tooltip]:max-w-[calc(100%-1rem)] [&_.ql-tooltip]:z-10 [&_.ql-tooltip_input]:!w-auto [&_.ql-tooltip_input]:flex-1 [&_.ql-tooltip_input]:min-w-0 [&_.ql-tooltip_input]:!m-0"
                    />
                ) : (
                    <div className="flex-1 flex items-center justify-center text-content-3 text-sm">
                        Đang tải nội dung...
                    </div>
                )}
            </div>

            {/* Sticky bottom action bar */}
            <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 bg-surface border-t shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
                {/* Save feedback */}
                <div className="flex-1">
                    {saved && (
                        <span className="text-green-600 text-sm font-medium animate-pulse">
                            ✓ Đã lưu thành công!
                        </span>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                    {postid && (
                        <a
                            href={`/post/${postid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded-lg border border-line-strong text-content-2 text-sm font-medium hover:bg-surface-sunken transition-colors"
                        >
                            Mở bài viết
                        </a>
                    )}
                    <button
                        disabled={saving}
                        onClick={() => {
                            if (postid) return onUpdatePost();
                            onSubmit();
                        }}
                        className="px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-w-[100px]"
                    >
                        {saving ? 'Đang lưu...' : postid ? 'Cập Nhật' : 'Tạo bài viết'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PostEditor;
